import React, { useEffect, useState } from 'react'
import { View, Text } from 'react-native'
import { Link, useRouter, useLocalSearchParams } from 'expo-router'
import { useAuth } from '../../hooks/useAuth'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { KeyboardAwareScrollView } from '../../components/ui/KeyboardAwareScrollView'
import { useToast } from '../../components/ui/Toast'
import { apiGet } from '../../lib/api'
import { REAL_NAME_MAX } from '@warcarp/api-types'

type UsernameStatus = 'idle' | 'invalid' | 'checking' | 'available' | 'taken' | 'error'

// Username constraints — mirror apps/api/src/routes/auth.ts so we don't
// fire pointless requests for inputs the server will reject anyway.
const USERNAME_MIN = 3
const USERNAME_MAX = 20
const USERNAME_RE = /^[a-zA-Z0-9_]+$/
const DEBOUNCE_MS = 400

export default function RegisterScreen() {
  const { register } = useAuth()
  const { showToast } = useToast()
  const router = useRouter()
  // Pre-fill from ?email=…&invitedBy=… — that's the URL the
  // app-invite email opens. invitedBy is shown as a one-line banner
  // so the new user knows who invited them.
  const search = useLocalSearchParams<{ email?: string; invitedBy?: string }>()
  const [email, setEmail] = useState(search.email ?? '')
  const invitedBy = typeof search.invitedBy === 'string' ? search.invitedBy : null
  const [username, setUsername] = useState('')
  const [realName, setRealName] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [usernameStatus, setUsernameStatus] = useState<UsernameStatus>('idle')

  // Debounced uniqueness check. Aborts in-flight requests when the user
  // keeps typing so we don't paint stale "available" hints on a stale value.
  useEffect(() => {
    if (!username) {
      setUsernameStatus('idle')
      return
    }
    if (
      username.length < USERNAME_MIN ||
      username.length > USERNAME_MAX ||
      !USERNAME_RE.test(username)
    ) {
      setUsernameStatus('invalid')
      return
    }
    setUsernameStatus('checking')
    const handle = setTimeout(async () => {
      try {
        const res = await apiGet<{ available: boolean }>(
          `/api/v1/auth/check-username?username=${encodeURIComponent(username)}`,
        )
        // Guard against a stale response: only commit if the field still
        // matches the value we asked about.
        setUsername((curr) => {
          if (curr === username) {
            setUsernameStatus(res.available ? 'available' : 'taken')
          }
          return curr
        })
      } catch {
        setUsernameStatus('error')
      }
    }, DEBOUNCE_MS)
    return () => clearTimeout(handle)
  }, [username])

  function validate() {
    const errs: Record<string, string> = {}
    if (!email) errs.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Invalid email'
    if (!username) errs.username = 'Username is required'
    else if (username.length < USERNAME_MIN)
      errs.username = `Username must be at least ${USERNAME_MIN} characters`
    else if (!USERNAME_RE.test(username))
      errs.username = 'Letters, numbers, and underscores only'
    else if (usernameStatus === 'taken') errs.username = 'Username is already taken'
    if (!password) errs.password = 'Password is required'
    else if (password.length < 8) errs.password = 'Password must be at least 8 characters'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  async function handleRegister() {
    if (!validate()) return
    setLoading(true)
    try {
      const res = await register({ email, username, password, realName: realName.trim() || undefined })
      router.replace({ pathname: '/(auth)/check-email', params: { email: res.email } })
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Registration failed'
      showToast(msg, 'error')
    } finally {
      setLoading(false)
    }
  }

  // Block submit while we're still checking the username, or if it's known-taken.
  // Letting the user submit during 'checking' would race the server check.
  const submitDisabled =
    loading || usernameStatus === 'checking' || usernameStatus === 'taken'

  // Live hint shown beneath the username field. The Input component
  // surfaces validation errors via `error`, which displays in red.
  // We additionally render a status line so "available" reads as
  // positive (olive-up), not validation noise.
  const usernameError =
    errors.username ?? (usernameStatus === 'taken' ? 'Username is already taken' : undefined)

  return (
    <KeyboardAwareScrollView
      className="flex-1 bg-canvas"
      contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', paddingHorizontal: 24, paddingVertical: 48 }}
    >
        <View className="gap-8">
          <View className="gap-2">
            <Text className="text-4xl text-ink font-stencil uppercase">Create Account</Text>
            <Text className="text-ink-soft font-typed">Join the battle</Text>
          </View>

          {invitedBy && (
            <Text className="font-typed text-ink-soft text-sm leading-6 text-center px-2">
              Invited by{' '}
              <Text className="font-stencil text-ink">{invitedBy}</Text>. Welcome.
            </Text>
          )}

          <View className="gap-4">
            <Input
              label="Email"
              value={email}
              onChangeText={setEmail}
              keyboardType="email-address"
              autoComplete="email"
              error={errors.email}
              // Enter / Return submits from any field, like an HTML form on desktop.
              onSubmitEditing={() => { if (!loading) void handleRegister() }}
            />
            <View>
              <Input
                label="Username"
                value={username}
                onChangeText={setUsername}
                autoComplete="username"
                error={usernameError}
                autoCapitalize="none"
                onSubmitEditing={() => { if (!loading) void handleRegister() }}
              />
              {!usernameError && username.length > 0 && (
                <Text
                  className={
                    usernameStatus === 'available'
                      ? 'font-typed text-olive-up text-xs mt-1'
                      : 'font-typed text-ink-soft text-xs mt-1 italic'
                  }
                  accessibilityLiveRegion="polite"
                >
                  {usernameStatus === 'checking' && '// checking availability…'}
                  {usernameStatus === 'available' && `// "${username}" is available`}
                  {usernameStatus === 'invalid' &&
                    `// ${USERNAME_MIN}–${USERNAME_MAX} chars, letters / numbers / underscores`}
                  {usernameStatus === 'error' && '// could not check — try again'}
                </Text>
              )}
            </View>
            <View className="gap-1">
              <Input
                label="Real name (optional)"
                value={realName}
                onChangeText={setRealName}
                placeholder="e.g. Alex"
                autoCapitalize="words"
                autoCorrect={false}
                maxLength={REAL_NAME_MAX}
                onSubmitEditing={() => { if (!loading) void handleRegister() }}
              />
              <Text className="font-typed text-ink-soft text-xs italic">
                // Optional. Shown on your public profile.
              </Text>
            </View>
            <Input
              label="Password"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              error={errors.password}
              returnKeyType="go"
              onSubmitEditing={() => { if (!loading) void handleRegister() }}
            />
          </View>

          <Button
            title="Create Account"
            onPress={handleRegister}
            loading={loading}
            disabled={submitDisabled}
            size="lg"
          />

          <Text className="text-ink-soft text-center font-typed">
            Already have an account?{' '}
            <Link href="/(auth)/login" className="text-blood font-semibold">
              Sign in
            </Link>
          </Text>
        </View>
    </KeyboardAwareScrollView>
  )
}
